import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

/** Lightbox a schermo intero. `items`: [{src,label}], `index`: indice aperto (null = chiuso). */
export const Lightbox = ({ items, index, onClose, onChange }) => {
  const open = index !== null && index >= 0 && index < items.length;
  const many = items.length > 1;

  const step = useCallback(
    (d) => many && onChange((index + d + items.length) % items.length),
    [index, items.length, many, onChange]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, step]);

  if (!open) return null;
  const item = items[index];

  return (
    <div
      data-testid="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={item.label}
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col"
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-4 sm:px-6 h-14 border-b border-neutral-800">
        <span className="mono-label text-xs text-neutral-400 uppercase tracking-wider">
          {many ? `${index + 1} / ${items.length}` : "Legend Team"}
        </span>
        <button
          type="button"
          data-testid="lightbox-close"
          onClick={onClose}
          aria-label="Chiudi"
          className="p-2 text-neutral-300 hover:text-red-500 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>
      <div className="relative flex-1 flex items-center justify-center p-4 sm:p-10 min-h-0">
        <img
          data-testid="lightbox-image"
          src={item.src}
          alt={item.label}
          className="max-w-full max-h-full object-contain border border-neutral-800 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
        {many && (
          <>
            <button
              type="button"
              data-testid="lightbox-prev"
              aria-label="Foto precedente"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 bg-neutral-900/80 hover:bg-red-600 border border-neutral-700 flex items-center justify-center text-white transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              type="button"
              data-testid="lightbox-next"
              aria-label="Foto successiva"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 bg-neutral-900/80 hover:bg-red-600 border border-neutral-700 flex items-center justify-center text-white transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>
      <p data-testid="lightbox-caption" className="px-6 py-4 text-center mono-label text-xs sm:text-sm uppercase tracking-wider text-neutral-200 border-t border-neutral-800">
        {item.label}
      </p>
    </div>
  );
};

export const useLightbox = () => {
  const [index, setIndex] = useState(null);
  return { index, open: setIndex, close: () => setIndex(null), change: setIndex };
};
